// NOTE: このファイルはまだちゃんとレビューされていません。コードの書き方を真似しないでください。
import type { FC } from "react"
import { useEffect, useRef, useState } from "react"

import ActionButton from "./ActionButton"
import MentionNodeTree, { type NodeTreeFocusRequest } from "./MentionNodeTree"
import {
	cx,
	formatDueDateLabel,
	formatRelativeTime,
	getAgeClassName,
	getDueDateBadgeClassName,
	getPriorityBadgeClassName,
	isEditableElement,
	openClaudeSession,
	parseSlackText,
} from "./utils"

import { FragmentType, getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

import styles from "../TaigaPage.module.scss"
import { useSessionSelection } from "../shared/SessionSelectionContext"

import type { SharedNode } from "../shared/NodeRow"

export const MentionRowFragment = graphql(/* GraphQL */ `
	fragment MentionRow on TaigaSlackMention {
		id
		channelId
		threadTs
		messageTs
		senderDisplayName
		channelName
		text
		permalink
		dueDate
		comment
		priority
		slackMessageTime
		embeddedNode {
			id
			name
			isDone
			session {
				sessionStatus
				isRemoteControlEnabled
				memo
				threadTs
				channelId
				ccSessionId
				claudeRemoteControlSessionId
				worktreePath
			}
		}
		isChildNodesCollapsed
		childNodesCount
		childNodes {
			id
			milestoneId
			parentNodeId
			name
			parallelPriority
			isArchived
			order
			isDone
			isCollapsed
			session {
				threadTs
				channelId
				sessionStatus
				isRemoteControlEnabled
				memo
				ccSessionId
				claudeRemoteControlSessionId
				worktreePath
			}
		}
	}
`)

export type MentionOverride = {
	dueDate?: string | null
	comment?: string
}

type MentionRowProps = {
	mention: FragmentType<typeof MentionRowFragment>
	// polling で取得した embedded session の最新 status (node id 単位)。
	polledSessionByNodeId: Record<
		string,
		{
			sessionStatus: string
			isRemoteControlEnabled: boolean
			memo: string | null
		}
	>
	override?: MentionOverride
	isFocused: boolean
	isEditingComment: boolean
	editingComment: string
	isReadOnly: boolean
	onFocus: () => void
	onOpenSlack: () => void
	onSetTomorrow: () => void
	onSetDueDate: () => void
	onPriorityUp: () => void
	onPriorityDown: () => void
	onStartEditComment: () => void
	onComplete: () => void
	onCompleteAll: () => void
	onChangeEditingComment: (comment: string) => void
	onSaveComment: () => void
	onCancelEditComment: () => void
	onToggleChildNodesCollapsed: (currentIsCollapsed?: boolean) => void
	onAddRootNode: () => void
	onHandOverEmbedSession?: () => void
	onUpdateNode: (id: string, name: string, isCollapsed?: boolean) => void
	onArchiveNode: (id: string) => void
	onAddChildNode: (milestoneId: string | null, parentNodeId: string) => void
	// Seq3: node ツリー操作ハンドラ
	onNodePriorityUp: (nodeId: string) => void
	onNodePriorityDown: (nodeId: string) => void
	onNodeCreateSession: (nodeId: string, nodeName: string) => void
	onNodeCompleteSession: (
		nodeId: string,
		nodeName: string,
		hasSession: boolean
	) => void
	onNodePendSession: (nodeId: string) => void
	onNodeOpenSlack: (nodeId: string, threadTs: string, channelId: string) => void
	onEmbeddedSessionOpenSlack: (channelId: string, threadTs: string) => void
	// embed session の Fork: フォーク先ノードはこの mention 直下の nodes (root node) に作る
	onEmbeddedSessionFork?: (nodeId: string, nodeName: string) => void
	onNodeCopyBranch: (nodeId: string) => void
	onNodeForkSession?: (nodeId: string, nodeName: string) => void
	onNodeOpenMemo: (node: SharedNode) => void
	onNodeMove: (
		nodeId: string,
		targetParentNodeId: string | null,
		afterNodeId: string | null
	) => void
	onNodeReorder: (parentNodeId: string | null, nodeIds: string[]) => void
	// Focus navigation for cross mention/tree keyboard nav
	onNodeFocusEscapeUp?: () => void
	onNodeFocusEscapeDown?: () => void
	nodeTreeFocusRequest?: NodeTreeFocusRequest
	// このメンションのツリーで node がフォーカスされたか（child → parent）。
	onNodeFocusChange?: (nodeId: string | null) => void
}

const MentionRow: FC<MentionRowProps> = ({
	mention: maskedMention,
	polledSessionByNodeId,
	override,
	isFocused,
	isEditingComment,
	editingComment,
	isReadOnly,
	onFocus,
	onOpenSlack,
	onSetTomorrow,
	onSetDueDate,
	onPriorityUp,
	onPriorityDown,
	onStartEditComment,
	onComplete,
	onCompleteAll,
	onChangeEditingComment,
	onSaveComment,
	onCancelEditComment,
	onToggleChildNodesCollapsed,
	onAddRootNode,
	onHandOverEmbedSession,
	onUpdateNode,
	onArchiveNode,
	onAddChildNode,
	onNodePriorityUp,
	onNodePriorityDown,
	onNodeCreateSession,
	onNodeCompleteSession,
	onNodePendSession,
	onNodeOpenSlack,
	onEmbeddedSessionOpenSlack,
	onEmbeddedSessionFork,
	onNodeCopyBranch,
	onNodeForkSession,
	onNodeOpenMemo,
	onNodeMove,
	onNodeReorder,
	onNodeFocusEscapeUp,
	onNodeFocusEscapeDown,
	nodeTreeFocusRequest,
	onNodeFocusChange,
}) => {
	const rowRef = useRef<HTMLDivElement>(null)
	const textareaRef = useRef<HTMLTextAreaElement>(null)
	const selection = useSessionSelection()
	const mention = getFragmentData(MentionRowFragment, maskedMention)
	// Claude アプリ (claude://code/{session-id}) でこの mention の embed session を開く
	// ための cc-server session id。未登録なら Claude ボタンは出さない。
	const embeddedCcSessionId = mention.embeddedNode?.session?.ccSessionId ?? null
	// claude.ai の会話ページを直接開くためのセッション ID (session_01...)。未解決なら null
	const embeddedClaudeRemoteControlSessionId =
		mention.embeddedNode?.session?.claudeRemoteControlSessionId ?? null
	// comment のアコーディオン展開状態。デフォルトはミニマイズ(畳んだ状態)。
	// mention 行クリックで toggle。focus が外れた時は閉じる。
	const [isCommentExpanded, setIsCommentExpanded] = useState(false)

	// Seq3: 他人タブ (read-only) 用の一時開閉状態。DB 永続化は自分タブのみ。
	const [localNodesExpanded, setLocalNodesExpanded] = useState(false)

	// 自分タブ: DB 値 (!isChildNodesCollapsed) を初期表示に使う
	// 他人タブ: ローカル一時 state で開閉のみ (永続化しない)
	const isNodesExpanded = isReadOnly
		? localNodesExpanded
		: !mention.isChildNodesCollapsed

	const dueDate =
		override && "dueDate" in override ? override.dueDate : mention.dueDate
	const comment =
		override && "comment" in override ? override.comment : mention.comment

	// embedded session の status / memo は polling 結果を優先し、なければ
	// fragment 取得時の値にフォールバックする (taiga sheet と同じ挙動)。
	const polledEmbeddedSession = mention.embeddedNode
		? polledSessionByNodeId[mention.embeddedNode.id]
		: undefined
	const embeddedSessionStatus =
		polledEmbeddedSession?.sessionStatus ??
		mention.embeddedNode?.session?.sessionStatus
	const isEmbeddedSessionRemoteControlEnabled =
		polledEmbeddedSession?.isRemoteControlEnabled ??
		mention.embeddedNode?.session?.isRemoteControlEnabled ??
		false
	const embeddedSessionMemo =
		polledEmbeddedSession?.memo ?? mention.embeddedNode?.session?.memo

	// childNodes を SharedNode[] に変換。
	// session の status / memo は polling 結果を優先し、なければ fragment 取得時の
	// 値にフォールバックする (embedded session と同じ挙動)。
	const childNodes: SharedNode[] = mention.childNodes.map((n) => {
		const polled = polledSessionByNodeId[n.id]
		return {
			id: n.id,
			milestoneId: n.milestoneId ?? null,
			parentNodeId: n.parentNodeId ?? null,
			name: n.name,
			parallelPriority: n.parallelPriority ?? null,
			isArchived: n.isArchived,
			order: n.order,
			isDone: n.isDone,
			isCollapsed: n.isCollapsed,
			session: n.session
				? {
						threadTs: n.session.threadTs,
						channelId: n.session.channelId,
						sessionStatus: polled?.sessionStatus ?? n.session.sessionStatus,
						isRemoteControlEnabled:
							polled?.isRemoteControlEnabled ??
							n.session.isRemoteControlEnabled,
						memo: polled?.memo ?? n.session.memo ?? null,
						ccSessionId: n.session.ccSessionId ?? null,
						claudeRemoteControlSessionId:
							n.session.claudeRemoteControlSessionId ?? null,
						worktreePath: n.session.worktreePath ?? null,
					}
				: null,
		}
	})

	useEffect(() => {
		if (!isFocused) {
			setIsCommentExpanded(false)
			return
		}
		rowRef.current?.scrollIntoView({ block: "nearest" })
	}, [isFocused])

	// 編集 textarea の高さをメモ本文の量に合わせて自動調整する。
	// 固定高さにせず、内容の行数分だけ縦に伸びるようにする。
	const adjustTextareaHeight = () => {
		const el = textareaRef.current
		if (!el) return
		el.style.height = "auto"
		el.style.height = `${el.scrollHeight}px`
	}

	// 編集開始時 (既存メモを読み込んだ直後) に高さを内容へフィットさせる。
	useEffect(() => {
		if (isEditingComment) {
			adjustTextareaHeight()
		}
	}, [isEditingComment])

	// 【3. mention 本体クリックでメモ最大化】
	// mention 行全体をクリックしたら comment(メモ)を toggle 表示。
	// Action button や node toggle 等は stopPropagation するため影響しない。
	const handleCardClick = () => {
		if (isEditingComment) return
		if (!isFocused) {
			onFocus()
		}
		setIsCommentExpanded((current) => !current)
	}

	// comment エリア直接クリックでも toggle (stopPropagation で handleCardClick と二重にならない)
	const handleCommentClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (isEditingComment) return
		if (!isFocused) {
			onFocus()
		}
		setIsCommentExpanded((current) => !current)
	}

	// 【4. node 部分クリックで展開】
	const handleNodesToggle = (e: React.MouseEvent) => {
		e.stopPropagation()
		if (isReadOnly) {
			setLocalNodesExpanded((v) => !v)
		} else {
			onToggleChildNodesCollapsed(mention.isChildNodesCollapsed)
		}
	}

	return (
		<div
			ref={rowRef}
			className={cx(styles.mentionRow, isFocused && styles.mentionRowFocused)}
			onClick={handleCardClick}
			tabIndex={0}
			role="button"
			onKeyDown={(event) => {
				if (isEditableElement(event.target)) return
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault()
					handleCardClick()
				}
			}}
		>
			<div className={styles.mentionTopLine}>
				<div className={styles.mentionMeta}>
					<span className={styles.channelBadge}>
						#{mention.channelName || mention.channelId}
					</span>
					{mention.threadTs && (
						<span className={styles.threadBadge}>In thread</span>
					)}
					<span
						className={cx(
							styles.priorityBadge,
							getPriorityBadgeClassName(mention.priority)
						)}
					>
						P{mention.priority}
					</span>
					<span className={styles.sender}>@{mention.senderDisplayName}</span>
					<span
						className={cx(
							styles.age,
							getAgeClassName(mention.slackMessageTime)
						)}
					>
						{formatRelativeTime(mention.slackMessageTime)}
					</span>
				</div>

				<div className={styles.mentionActions}>
					{dueDate && (
						<span
							className={cx(
								styles.deferredBadge,
								getDueDateBadgeClassName(dueDate)
							)}
						>
							期限 {formatDueDateLabel(dueDate)}
						</span>
					)}
					{mention.permalink && (
						<ActionButton
							label="Slack"
							shortcut="1"
							variant="primary"
							onClick={onOpenSlack}
						/>
					)}
					{!isReadOnly && (
						<>
							<ActionButton
								label="明日"
								shortcut="2"
								variant="outline"
								onClick={onSetTomorrow}
							/>
							<ActionButton
								label="P↑"
								shortcut="3"
								variant="outline"
								onClick={onPriorityUp}
							/>
							<ActionButton
								label="P↓"
								shortcut="4"
								variant="outline"
								onClick={onPriorityDown}
							/>
							<ActionButton
								label="期限"
								shortcut="5"
								variant="outline"
								onClick={onSetDueDate}
							/>
							<ActionButton
								label="メモ"
								shortcut="E"
								variant="outline"
								onClick={onStartEditComment}
							/>
							<ActionButton
								label="完了"
								shortcut="Q"
								variant="success"
								onClick={onComplete}
							/>
							<ActionButton
								label="全完了"
								variant="success"
								onClick={onCompleteAll}
							/>
						</>
					)}
				</div>
			</div>

			{/* メモ表示エリア。編集中は同じ枠をそのまま編集 UI に差し替える
			    (別セクションを追加せず、表示枠を有効活用する)。 */}
			{isEditingComment ? (
				<div
					className={styles.commentEditor}
					onClick={(event) => event.stopPropagation()}
				>
					<textarea
						ref={textareaRef}
						className={styles.commentTextarea}
						value={editingComment}
						onChange={(event) => {
							onChangeEditingComment(event.currentTarget.value)
							adjustTextareaHeight()
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
								event.preventDefault()
								onSaveComment()
							} else if (event.key === "Escape") {
								event.preventDefault()
								onCancelEditComment()
							}
						}}
						autoFocus
					/>
					<div className={styles.commentEditorFooter}>
						<span className={styles.commentEditorHint}>
							Cmd+Enter で保存 / Esc でキャンセル
						</span>
						<div className={styles.commentEditorActions}>
							<button
								type="button"
								className={styles.cancelButton}
								onClick={onCancelEditComment}
							>
								キャンセル
							</button>
							<button
								type="button"
								className={styles.saveButton}
								onClick={onSaveComment}
							>
								保存
							</button>
						</div>
					</div>
				</div>
			) : (
				<div
					className={styles.mentionMemo}
					data-expanded={isCommentExpanded}
					onClick={handleCommentClick}
				>
					{comment
						? parseSlackText(comment).map((segment, index) =>
								segment.type === "link" ? (
									<a
										key={index}
										href={segment.url}
										target="_blank"
										rel="noopener noreferrer"
										className={styles.mentionMemoLink}
										onClick={(event) => event.stopPropagation()}
									>
										{segment.label}
									</a>
								) : (
									<span key={index}>{segment.value}</span>
								)
							)
						: null}
				</div>
			)}

			{/* 【5. embed と node 両方ある場合のレイアウト】 */}
			{/* 埋め込み node + session (Seq2) — node ツリーの上に配置 */}
			{/* done 済み node はなきもの扱い（backend も返さないが、Done 直後の cache 反映用に FE でも弾く） */}
			{mention.embeddedNode?.session &&
				!mention.embeddedNode.isDone &&
				embeddedSessionStatus && (
					<div
						className={styles.embeddedSession}
						onClick={(event) => {
							event.stopPropagation()
							const session = mention.embeddedNode?.session
							if (!session) return
							onEmbeddedSessionOpenSlack(session.channelId, session.threadTs)
						}}
					>
						{selection && (
							// span でクリック当たり判定を広げつつ stopPropagation する。
							// checkbox 本体は小さく、少し外すと親の onClick (Slack を開く) が
							// 発火してしまう。disabled 時も span がクリックを吸収する。
							<span
								className={styles.sessionSelectCheckboxWrap}
								onClick={(event) => event.stopPropagation()}
							>
								<input
									type="checkbox"
									className={styles.sessionSelectCheckbox}
									disabled={mention.embeddedNode.session.ccSessionId === null}
									title={
										mention.embeddedNode.session.ccSessionId === null
											? "cc-server の session id が未登録のため選択できません"
											: undefined
									}
									checked={
										mention.embeddedNode.session.ccSessionId !== null &&
										selection.selectedCcSessions.has(
											mention.embeddedNode.session.ccSessionId
										)
									}
									onChange={() => {
										const node = mention.embeddedNode
										const ccSessionId = node?.session?.ccSessionId
										if (node && ccSessionId) {
											selection.toggleCcSession({
												ccSessionId,
												name: `p${mention.priority}:${node.name}`,
												worktreePath: node.session?.worktreePath ?? null,
											})
										}
									}}
								/>
							</span>
						)}
						<span
							className={cx(
								styles.embeddedSessionStatus,
								styles[embeddedSessionStatus.toLowerCase()]
							)}
						>
							{embeddedSessionStatus.toLowerCase()}
						</span>
						<span className={styles.embeddedSessionNodeName}>
							{mention.embeddedNode.name}
						</span>
						{embeddedSessionMemo && (
							<span className={styles.embeddedSessionMemo}>
								{embeddedSessionMemo.length > 50
									? `${embeddedSessionMemo.slice(0, 50)}...`
									: embeddedSessionMemo}
							</span>
						)}
						{/* Claude ボタンは RC (Remote Control) 有効セッションだけに出す。
					    RC バッジは廃止し、このボタンの有無が RC の目印を兼ねる。 */}
						{isEmbeddedSessionRemoteControlEnabled && embeddedCcSessionId && (
							<button
								type="button"
								className={styles.embeddedSessionClaudeBtn}
								title="Claude アプリでこのセッションを開く (Remote Control 有効セッション)"
								onClick={(event) => {
									event.stopPropagation()
									openClaudeSession(
										embeddedClaudeRemoteControlSessionId,
										embeddedCcSessionId
									)
								}}
							>
								Claude
							</button>
						)}
						{!isReadOnly && onEmbeddedSessionFork && (
							<button
								type="button"
								className={styles.embeddedSessionForkBtn}
								title="embed session をフォーク（この mention 直下の nodes に worktree・会話履歴を引き継いだ複製セッションを作成）"
								onClick={(event) => {
									event.stopPropagation()
									const node = mention.embeddedNode
									if (!node) return
									onEmbeddedSessionFork(node.id, node.name)
								}}
							>
								Fork
							</button>
						)}
						{!isReadOnly && (
							<button
								type="button"
								className={styles.embeddedSessionDoneBtn}
								title="embed session を完了（node を done にし、スレッドに :done: を投稿）"
								onClick={(event) => {
									event.stopPropagation()
									const node = mention.embeddedNode
									if (!node) return
									onNodeCompleteSession(node.id, node.name, true)
								}}
							>
								Done
							</button>
						)}
					</div>
				)}

			{/* Seq3: node ツリーアコーディオン (埋め込み session の下) */}
			{(!isReadOnly || mention.childNodesCount > 0) && (
				// nodes エリア内のクリック (toggle・ツリー行・余白) が mention 行の
				// onClick (メモ toggle) まで伝播すると、node 操作のたびにメモが
				// 開閉してしまうため、コンテナで伝播を止める。
				<div
					className={styles.mentionNodes}
					onClick={(event) => event.stopPropagation()}
				>
					<button
						type="button"
						className={styles.mentionNodesToggle}
						onClick={handleNodesToggle}
					>
						{isNodesExpanded ? "▾" : "▸"} {mention.childNodesCount} nodes
					</button>
					{isNodesExpanded && (
						<MentionNodeTree
							nodes={childNodes}
							mentionId={mention.id}
							isReadOnly={isReadOnly}
							onUpdateNode={onUpdateNode}
							onArchiveNode={onArchiveNode}
							onAddChildNode={onAddChildNode}
							onAddRootNode={onAddRootNode}
							onHandOverEmbedSession={onHandOverEmbedSession}
							onPriorityUp={onNodePriorityUp}
							onPriorityDown={onNodePriorityDown}
							onCreateSession={onNodeCreateSession}
							onCompleteSession={onNodeCompleteSession}
							onPendSession={onNodePendSession}
							onOpenSlack={onNodeOpenSlack}
							onCopyBranch={onNodeCopyBranch}
							onForkSession={onNodeForkSession}
							onOpenMemo={onNodeOpenMemo}
							onMoveNode={onNodeMove}
							onReorderNodes={onNodeReorder}
							onFocusEscapeUp={onNodeFocusEscapeUp}
							onFocusEscapeDown={onNodeFocusEscapeDown}
							focusRequest={nodeTreeFocusRequest}
							isParentMentionFocused={isFocused}
							onFocusedNodeChange={onNodeFocusChange}
							sessionNameSuffix={`p${mention.priority}`}
						/>
					)}
				</div>
			)}
		</div>
	)
}

export default MentionRow
